import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Target, TrendingUp, Brain, Users, GraduationCap, Award, Star, Lightbulb, Users2, MonitorPlay } from 'lucide-react';

// Highlight component for important keywords
const Highlight = ({ children }) => (
  <span className="font-bold text-blue-700 dark:text-blue-400">
    {children}
  </span>
);
import { Button } from '@/components/ui/button';

const HomePage = () => {
  const features = [
    {
      icon: Users2,
      title: 'Expert Mentorship',
      description: 'Guidance from experienced educators to help you overcome challenges and excel in physics.'
    },
    
    {
      icon: BookOpen,
      title: 'Structured Study Resources',
      description: 'Access crisp, well-organized notes, DPPs, and revision sheets — built to simplify tough concepts, not overwhelm you.'
    },
    {
      icon: Target,
      title: 'Daily Practice Problems',
      description: 'Curated DPPs (Daily Practice Problems) to strengthen your problem-solving skills.'
    },
    
    {
      icon: MonitorPlay,
      title: 'Personalized Study Plans',
      description: 'Get weekly or monthly plans customized to your syllabus, pace, and exam goals — so you always know what to study next.'
    },
    {
      icon: TrendingUp,
      title: 'Regular Test Series',
      description: 'Evaluate your progress with realistic mock tests and topic-wise quizzes, and get feedback to improve where it matters most.'
    },
    {
      icon: Brain,
      title: 'Clarity. Strategy. Support.',
      description: 'Because mentorship isn’t just about teaching — it’s about helping you think like a ranker.'
    }
  ];

  const targetAudience = [
    {
      icon: Users,
      title: 'School Students',
      description: (
        <>
          Structured guidance and resources for school and <Highlight>Class 10 & 12</Highlight> board exams preparation.
        </>
      )
    },
    {
      icon: GraduationCap,
      title: 'Entrance Aspirants',
      description: (
        <>
          Targeted mentorship for <Highlight>JEE</Highlight>, <Highlight>NEET</Highlight>, and other <Highlight>entrance exams</Highlight> — with strategy, tests, and feedback.
        </>
      )
    },
    {
      icon: Lightbulb,
      title: 'Learners',
      description: 'For those who simply love learning Physics the right way — no pressure, just curiosity.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Complete Mentorship – Clarity, Strategy, and Support.</title>
        <meta name="description" content="Comprehensive physics learning platform for Class 11/12, JEE, NEET, and Olympiad preparation. Get detailed notes, daily practice problems, and AI-powered learning tools." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              Complete Mentorship
              <span className="gradient-text block">Clarity, Strategy, and Support</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"
            >
            Learn how to study smarter, plan better, and score higher. <br/> Whether you're preparing for <Highlight>Boards</Highlight>, <Highlight>JEE</Highlight>, <Highlight>NEET</Highlight>, or any other <Highlight>Physics exam</Highlight> — we've got you covered.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a 
                href="/dlp" 
                target="_blank" 
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Button className="btn-primary text-white px-8 py-3 text-lg">
                  Explore Learning Solutions
                </Button>
              </a>
              <a 
                href="https://blog.physicsnotebook.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Button variant="outline" className="px-8 py-3 text-lg">
                  Read Our Blog
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose PhysicsNotebook.in?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Because you don’t just need notes — you need guidance, structure, and accountability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="bg-gradient-to-r from-blue-600 to-green-500 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect For Every Physics Learner
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From acing your school exams to cracking competitive tests, our resources are tailored for every physics learner's journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={audience.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-xl p-8 shadow-lg card-hover text-center"
              >
                <div className="bg-gradient-to-r from-blue-600 to-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <audience.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{audience.title}</h3>
                <p className="text-gray-600">
                  {typeof audience.description === 'string' ? audience.description : audience.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-green-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Physics Learning?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join other students who are already excelling in physics with our comprehensive platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/solutions">
                <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold">
                  Start Learning Now
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 text-lg">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
